// Notion "Idea - Stripe VAT" §3.2, line for line. Every input combination is
// a Matrix A/B row or a block; the function never throws on a legal input
// and never returns undefined. CommonJS: shared by server and client.
const { REGION, region, vatCountry } = require('./regions');
const { standardRate } = require('./rates');
const { NOTE } = require('./notes');

const VAT_STATUS = {
  REGISTERED: 'REGISTERED',
  EXEMPT_SMALL_BUSINESS: 'EXEMPT_SMALL_BUSINESS',
  INVOICING_SERVICE: 'INVOICING_SERVICE',
};
const CUSTOMER_TYPE = { BUSINESS: 'BUSINESS', CONSUMER: 'CONSUMER' };
const VALIDATION = {
  VERIFIED: 'VERIFIED',
  UNVERIFIED: 'UNVERIFIED',
  PENDING: 'PENDING',
  NONE: 'NONE',
};
const BLOCK = {
  CROSS_BORDER_B2B_NEEDS_SELLER_VAT_ID: 'CROSS_BORDER_B2B_NEEDS_SELLER_VAT_ID',
  EU_SELLER_TO_ALAND_CONSUMER: 'EU_SELLER_TO_ALAND_CONSUMER',
  SELLER_NOT_A_BUSINESS: 'SELLER_NOT_A_BUSINESS',
};

// Freedomly Oy, fixed on one side of every supply on the invoice and light paths.
const FREEDOMLY_AS_SELLER = {
  country: 'FI',
  postalCode: '00180',
  vatStatus: VAT_STATUS.REGISTERED,
  vatNumber: 'FI31503663',
  homeVat: null,
  exemptId: null,
};
const FREEDOMLY_AS_BUYER = {
  country: 'FI',
  postalCode: '00180',
  customerType: CUSTOMER_TYPE.BUSINESS,
  vatNumber: 'FI31503663',
  vatValidationStatus: VALIDATION.VERIFIED,
  businessProofRef: '3150366-3',
};

// Is the client a taxable person for our purposes? (§3.2 clientIsTaxable)
const clientIsTaxable = c => {
  if (!c || c.customerType !== CUSTOMER_TYPE.BUSINESS) return false;
  const r = region(c);
  if (r === REGION.FI || r === REGION.EU) return c.vatValidationStatus === VALIDATION.VERIFIED;
  return !!c.businessProofRef; // AX, NON_EU
};

const exemptNote = country =>
  country === 'FI' ? NOTE.EXEMPT_FI : country === 'DE' ? NOTE.EXEMPT_DE : NOTE.EXEMPT_EU;

const sellerLabel = f => (region(f) === REGION.NON_EU && f.homeVat && f.homeVat.label) || 'VAT';

const isRegistered = f =>
  f.vatStatus === VAT_STATUS.REGISTERED || f.vatStatus === VAT_STATUS.INVOICING_SERVICE;

// Supplier-country rule (§3.2 domestic)
const domestic = f => {
  const r = region(f);
  if (isRegistered(f)) {
    const rate =
      r === REGION.NON_EU
        ? f.homeVat
          ? Number(f.homeVat.rate) || 0
          : 0
        : standardRate(vatCountry(f));
    return {
      rate,
      label: sellerLabel(f),
      noteKey: rate > 0 ? NOTE.STANDARD : NOTE.NON_EU_SELLER_NO_VAT,
    };
  }
  if (f.vatStatus === VAT_STATUS.EXEMPT_SMALL_BUSINESS) {
    return { rate: 0, label: 'VAT', noteKey: exemptNote(vatCountry(f)) };
  }
  return { blocked: BLOCK.SELLER_NOT_A_BUSINESS };
};

const finish = (res, kase, placeOfSupply) =>
  res.blocked
    ? res
    : { case: kase, rate: res.rate, label: res.label, noteKey: res.noteKey, placeOfSupply };

const zero = (kase, noteKey, label, placeOfSupply) => ({
  case: kase,
  rate: 0,
  label,
  noteKey,
  placeOfSupply,
});

// Core service: seller f (freelancer or Freedomly), buyer c (client or Freedomly).
const resolveSupply = (f, c) => {
  // An absent or unrecognised status must never fall through to the
  // cross-border rows (which would resolve to 0 % reverse charge).
  if (!f || !Object.values(VAT_STATUS).includes(f.vatStatus)) {
    return { blocked: BLOCK.SELLER_NOT_A_BUSINESS };
  }
  const fr = region(f);
  const cr = region(c);
  const fCountry = vatCountry(f);
  const cCountry = vatCountry(c);
  const taxable = clientIsTaxable(c);

  // 1. Same country (FI and AX both count as FI)
  if (fCountry === cCountry) {
    const d = domestic(f);
    const kase =
      fr === REGION.NON_EU
        ? 'CS-12'
        : fr === REGION.AX || cr === REGION.AX
        ? 'AX-1'
        : d.rate > 0
        ? taxable
          ? 'CS-1'
          : 'CS-3'
        : taxable
        ? 'CS-2'
        : 'CS-4';
    return finish(d, kase, fCountry);
  }

  // 2. Seller established in the EU VAT area (FI or EU, not AX)
  if (fr === REGION.FI || fr === REGION.EU) {
    if (taxable) {
      if (cr === REGION.EU || (cr === REGION.FI && fr === REGION.EU)) {
        if (!f.vatNumber) return { blocked: BLOCK.CROSS_BORDER_B2B_NEEDS_SELLER_VAT_ID };
        return zero('CS-5', NOTE.REVERSE_CHARGE, 'VAT', cCountry);
      }
      return zero('CS-8', NOTE.OUTSIDE_EU_B2B, 'VAT', cCountry); // NON_EU or AX business
    }
    if (cr === REGION.AX && fr === REGION.EU) return { blocked: BLOCK.EU_SELLER_TO_ALAND_CONSUMER };
    const d = domestic(f);
    const kase =
      cr === REGION.EU || cr === REGION.FI
        ? d.rate > 0
          ? 'CS-6'
          : 'CS-7'
        : cr === REGION.AX
        ? 'AX-3'
        : 'CS-9';
    return finish(d, kase, fCountry);
  }

  // 3. Seller established in Åland (third territory); cCountry !== 'FI' here
  if (fr === REGION.AX) {
    if (taxable) return zero('AX-2', NOTE.REVERSE_CHARGE_NON_EU_SELLER, 'VAT', cCountry);
    return finish(domestic(f), 'AX-3', 'FI');
  }

  // 4. Seller established outside the EU
  const label = sellerLabel(f);
  if (taxable) {
    if (cr === REGION.NON_EU) return zero('CS-13', NOTE.NON_EU_SELLER_NO_EU_VAT, label, cCountry);
    return zero('CS-10', NOTE.REVERSE_CHARGE_NON_EU_SELLER, label, cCountry);
  }
  return zero('CS-11', NOTE.NON_EU_SELLER_NO_EU_VAT, label, fCountry);
};

// Platform fee: seller = Freedomly (FI, REGISTERED), buyer = freelancer f. Stripe-direct only.
const resolvePlatformFee = f => {
  const r = region(f);
  const fi = standardRate('FI');
  if (r === REGION.FI || r === REGION.AX)
    return { case: 'PF-1', rate: fi, label: 'VAT', noteKey: NOTE.STANDARD, placeOfSupply: 'FI' };
  if (r === REGION.EU) {
    return f.vatNumber
      ? zero('PF-2', NOTE.REVERSE_CHARGE, 'VAT', vatCountry(f))
      : { case: 'PF-3', rate: fi, label: 'VAT', noteKey: NOTE.STANDARD, placeOfSupply: 'FI' };
  }
  return zero('PF-4', NOTE.OUTSIDE_EU_B2B, 'VAT', vatCountry(f));
};

module.exports = {
  VAT_STATUS,
  CUSTOMER_TYPE,
  VALIDATION,
  BLOCK,
  FREEDOMLY_AS_SELLER,
  FREEDOMLY_AS_BUYER,
  clientIsTaxable,
  resolveSupply,
  resolvePlatformFee,
};
